import { getBootstrapData } from "@swa-ui/bootstrap";
import { window } from "@swa-ui/browser/window";

import {
  getPersistedSmartblockStateData,
  resetPersistedSmartblockStateData,
} from "../carPersistence";
import {
  CARTRAWLER_SCRIPT_ELEMENT_ID,
  CARTRAWLER_SECTION_ID,
  LANGUAGE_CODES,
  PARAMETER_PARTNER_CODES_ENVIRONMENT,
} from "../defines";

const PROD_HOSTNAME_REGEX = /^(www|espanol|mobile|mobile-offline)\.(southwest|swabiz)\.com$/;

/**
 * @typedef {Object} Options
 * @property {String} behavior Indicates how the UI section should function.
 * @property {object} benefits Benefits applicable for the customer including promo codes, etc.
 * @property {String} context Type of UI section is displyed based on the context value.
 * @property {object} customer Customer details.
 * @property {object} flight Flight details for all legs.
 * @property {Function} onAddCarToBasket Event handler for when a car is added to the shopping basket.
 * @property {Function} onBackToExtras Event handler for back button click action in the SmartBlock.
 * @property {Function} onError Event handler triggered when the Smartblock script fails to load.
 * @property {Function} onRemoveCarFromBasket Event handler for when car is removed from shopping basket.
 * @property {Function} onSearch Custom event handler for search/view action.
 * @property {String} pageType Indicates page navigation behavior.
 * @property {Object} parameters Custom parameters, primarliy for corporate configuration.
 * @property {object} passengers Passengers count for various passenger type.
 * @property {Boolean} shouldInitializeWithPersistedData Indicates if CTStore should use existing data in session storage.
 * @property {Boolean} shouldSetSessionData Indicates if user selections should be saved in session storage on submit.
 * @property {String} reservationID Confirmation number for the reservation.
 * @property {Array} suppliersAllowed Array of allowed car rental suppliers.
 * @property {String} touchpoint Entry point identifier of the flow.
 */

/**
 * This function sets all the required elements needed for rendering a Smartblock.
 * @param {Options} options
 */
export const loadCarSection = (options) => {
  setupCTStore(options);
  loadSmartBlockScript(options);
};

/**
 * CTStore is a window object which needs to be present before Smartblock script is executed.
 * The Smartblock script updates this object automatically on user actions.
 * @param {Options} options
 */
const setupCTStore = (options = {}) => {
  const {
    behavior,
    benefits,
    context,
    customer,
    flight,
    pageType,
    parameters,
    passengers,
    reservationID,
    shouldInitializeWithPersistedData = false,
    suppliersAllowed,
    touchpoint,
  } = options;

  const transformedParameters = transformParameters(parameters);
  const language = LANGUAGE_CODES[window.MP?.UrlLang ?? "en"];
  const storeData = {
    dataSource: {
      currency: "USD",
      custom: {
        ...(behavior && { behavior }),
        ...(context && { context }),
        ...(pageType && { pageType }),
        ...(transformedParameters && { parameters: transformedParameters }),
        ...(suppliersAllowed && { suppliersAllowed }),
        ...(touchpoint && { touchpoint }),
      },
      ...(customer && { customer }),
      ...(flight && { flight }),
      language,
      ...(passengers && { passengers }),
      ...(benefits && { benefits }),
      ...(reservationID && { rentals: { basket: [{ reservationID }] } }),
    },
  };
  const persistedSmartblockState = getPersistedSmartblockStateData();

  if (shouldInitializeWithPersistedData && persistedSmartblockState) {
    if (!window.CTStore) {
      window.CTStore = {};
    }

    window.CTStore.dataSource = persistedSmartblockState.dataSource || {};
  } else {
    resetPersistedSmartblockStateData();

    if (window.CTStore) {
      window.CTStore.dataSource = storeData.dataSource;
    } else {
      window.CTStore = storeData;
    }
  }
};

/**
 * Transforms the parameters object conditionally and returns the updated parameters.
 * @param {*} parameters
 * @returns {Object} Transformed parameters object.
 */
const transformParameters = (parameters) => {
  if (!isProd()) {
    const { hostname } = window?.location || {};
    const lowerEnvironment = hostname?.split(".")?.[1];

    if (!parameters) {
      parameters = {};
    }

    if (!parameters.partnerCodes) {
      parameters.partnerCodes = [];
    }

    parameters.partnerCodes.push({
      type: PARAMETER_PARTNER_CODES_ENVIRONMENT,
      value: lowerEnvironment,
    });
  }

  return parameters;
};

/**
 * This function creates the HTML script element with required attributes and adds it to the DOM
 * @param {Options} options
 */
const loadSmartBlockScript = (options = {}) => {
  const { onError, waitForElement } = options;
  const doc = window.document;
  const existingScriptId = doc.getElementById(CARTRAWLER_SCRIPT_ELEMENT_ID);
  const { CARTRAWLER_SCRIPT_SRC_NON_PROD, CARTRAWLER_SCRIPT_SRC_PROD } = getBootstrapData("urls");
  const scriptSource = isProd() ? CARTRAWLER_SCRIPT_SRC_PROD : CARTRAWLER_SCRIPT_SRC_NON_PROD;

  if (!existingScriptId) {
    const script = doc.createElement("script");

    script.type = "text/javascript";
    script.src = scriptSource;
    script.async = true;
    script.id = CARTRAWLER_SCRIPT_ELEMENT_ID;
    script.defer = true;
    script.onerror = function () {
      console.error("Failed to load the Smartblock script.");
      window.loading_ct_smartblock = false;
      script.remove();
      // TODO: create an ErrorState component in swa-ui-car package when we wont have the Vision dependency. (SPASHOP-877)
      onError?.();
    };

    if (!window.loading_ct_smartblock) {
      window.loading_ct_smartblock = true;
      waitForElement ? lookForSmartBlock(script) : executeSmartBlockScript(script);
    }
  } else {
    waitForElement ? lookForSmartBlock() : executeSmartBlockScript();
  }
};

/**
 * Passively looks for the Smartblock element in the DOM.
 * This guarantees that the script is executed only after the element is commited to the DOM first, outside of React lifecycles.
 * @param {*} script
 */
const lookForSmartBlock = (script) => {
  // TODO: Add a timeout if element is not found for longer than expected time. (Probably 100ms)
  const doc = window.document;
  const domObserver = new MutationObserver((_, observer) => {
    const smartblock = doc.getElementById(CARTRAWLER_SECTION_ID);

    if (smartblock) {
      observer.disconnect();
      executeSmartBlockScript(script);
    }
  });

  domObserver.observe(doc.body, { childList: true, subtree: true });
};

/**
 * This appends the JS script in the DOM. ALternatively, it restarts the vendor SBIP function if script is not provided in the argument.
 * Both the options result in execution of the Smartblock script.
 * @param {*} script
 */
const executeSmartBlockScript = (script) => {
  const doc = window.document;

  if (script) {
    doc.head.appendChild(script);
  } else {
    window.CTStore?.restartSbip?.();
  }
};

// TODO: Explore using getEnvironmentTier from swa-ui-dotcom package when the cars package is used only by Nextgen SPAs. HYPE-1330
const isProd = () => {
  const { hostname } = window?.location ?? { hostname: "www.southwest.com" };

  return PROD_HOSTNAME_REGEX.test(hostname);
};
